import ApiService from "service/Api";
import { CatalogPayloadTypes } from "utilities/types/Catalog";
import { CatalogDescriptionPayloadTypes } from "utilities/types/CatalogDescription";

const CatalogService = {
  getAllCatalogs: async (
    page: number,
    search: string,
    limit: number,
    filterTenant: number
  ) => {
    const requestData = {
      method: "get",
      headers: {
        "Content-Type": "application/json; charset=utf-8",
      },
      params: {
        is_voucher: 0,
        search: search,
        page: page,
        per_page: limit,
        filter_tenant: filterTenant,
        portal: "admin",
      },
      url: "admin/catalogs",
    };

    try {
      const res = await ApiService.apiRequest(requestData);
      const data = res.data.data;

      return data;
    } catch (error) {
      throw error;
    }
  },

  getCatalogById: async (catalogId: string) => {
    const requestData = {
      method: "get",
      headers: {
        "Content-Type": "application/json; charset=utf-8",
      },
      url: "admin/catalogs/" + catalogId,
    };

    try {
      const res = await ApiService.apiRequest(requestData);
      const data = res.data.data;

      return data;
    } catch (error) {
      throw error;
    }
  },

  getAllCatalogCategories: async () => {
    const endpoints = ["admin/catalog-categories", "admin/catalogs/categories"];

    for (const endpoint of endpoints) {
      try {
        const requestData = {
          method: "get",
          headers: {
            "Content-Type": "application/json; charset=utf-8",
          },
          url: endpoint,
        };

        const res = await ApiService.apiRequest(requestData);
        return res.data.data;
      } catch (error) {
        continue;
      }
    }

    throw new Error("Catalog categories endpoint is not available");
  },

  storeCatalogCategory: async (payload: {
    categoryName: string;
    isAllUser?: boolean;
    tenantId?: string | number | null;
  }) => {
    const requestData = {
      method: "post",
      headers: {
        "Content-Type": "application/json; multipart/form-data; charset=utf-8",
      },
      data: {
        name: payload.categoryName,
        is_all_user: Boolean(payload.isAllUser),
        tenant_id: payload.tenantId ?? null,
      },
      url: "admin/catalog-categories",
    };

    try {
      const res = await ApiService.apiRequest(requestData);
      return res.data.data;
    } catch (error) {
      throw error;
    }
  },

  updateCatalogCategory: async (
    categoryId: string,
    payload: {
      categoryName: string;
      isAllUser?: boolean;
      tenantId?: string | number | null;
    }
  ) => {
    const requestData = {
      method: "post",
      headers: {
        "Content-Type": "application/json; multipart/form-data; charset=utf-8",
      },
      data: {
        name: payload.categoryName,
        is_all_user: Boolean(payload.isAllUser),
        tenant_id: payload.tenantId ?? null,
        _method: "PUT",
      },
      url: `admin/catalog-categories/${categoryId}`,
    };

    try {
      const res = await ApiService.apiRequest(requestData);
      return res.data.data;
    } catch (error) {
      throw error;
    }
  },

  deleteCatalogCategory: async (categoryId: string) => {
    const requestData = {
      method: "delete",
      headers: {
        "Content-Type": "application/json; charset=utf-8",
      },
      url: `admin/catalog-categories/${categoryId}`,
    };

    try {
      const res = await ApiService.apiRequest(requestData);
      return res.data.data;
    } catch (error) {
      throw error;
    }
  },

  storeCatalog: async (payload: CatalogPayloadTypes) => {
    const {
      catalogName,
      pointsNeeded,
      catalogImage,
      expiryDate,
      catalogType,
      catalogProduct,
      pointTypeUsed,
      tenantId,
      categoryId,
      isActive,
    } = payload;

    const requestData = {
      method: "post",
      headers: {
        "Content-Type": "application/json; multipart/form-data; charset=utf-8",
      },
      data: {
        catalog_name: catalogName,
        points_needed: pointsNeeded,
        image: catalogImage,
        expiry_date: expiryDate,
        catalog_type: catalogType,
        is_voucher: 0,
        catalog_product: catalogProduct,
        point_type_used: pointTypeUsed,
        tenant_id: tenantId,
        category_id: categoryId,
        is_active: isActive,
      },
      url: "admin/catalogs",
    };

    try {
      const res = await ApiService.apiRequest(requestData);
      const data = res.data.data;

      return data;
    } catch (error) {
      throw error;
    }
  },

  updateCatalog: async (catalogId: string, payload: CatalogPayloadTypes) => {
    const {
      catalogName,
      pointsNeeded,
      catalogImage,
      expiryDate,
      catalogType,
      catalogProduct,
      pointTypeUsed,
      tenantId,
      categoryId,
      isActive,
    } = payload;

    const requestData = {
      method: "post",
      headers: {
        "Content-Type": "application/json; multipart/form-data; charset=utf-8",
      },
      data: {
        catalog_name: catalogName,
        points_needed: pointsNeeded,
        image: catalogImage,
        expiry_date: expiryDate,
        catalog_type: catalogType,
        is_voucher: 0,
        catalog_product: catalogProduct,
        point_type_used: pointTypeUsed,
        tenant_id: tenantId,
        category_id: categoryId,
        is_active: isActive,
        _method: "PUT",
      },
      url: "admin/catalogs/" + catalogId,
    };

    try {
      const res = await ApiService.apiRequest(requestData);
      const data = res.data.data;

      return data;
    } catch (error) {
      throw error;
    }
  },

  deleteCatalog: async (catalogId: string) => {
    const requestData = {
      method: "delete",
      headers: {
        "Content-Type": "application/json; charset=utf-8",
      },
      url: "admin/catalogs/" + catalogId,
    };

    try {
      const res = await ApiService.apiRequest(requestData);
      const data = res.data.data;

      return data;
    } catch (error) {
      throw error;
    }
  },

  updateCatalogDescription: async (payload: CatalogDescriptionPayloadTypes) => {
    const {
      catalogId,
      title,
      termsAndCondition,
      purcaseAndUse,
      titleEn,
      termsAndConditionEn,
      purcaseAndUseEn,
    } = payload;

    const requestData = {
      method: "post",
      headers: {
        "Content-Type": "application/json; multipart/form-data; charset=utf-8",
      },
      data: {
        catalog_id: catalogId,
        title: title,
        terms_and_condition: termsAndCondition,
        purcase_and_use: purcaseAndUse,
        title_en: titleEn,
        terms_and_condition_en: termsAndConditionEn,
        purcase_and_use_en: purcaseAndUseEn,
        _method: "PUT",
      },
      url: "admin/catalogs-description",
    };

    try {
      const res = await ApiService.apiRequest(requestData);
      const data = res.data.data;

      return data;
    } catch (error) {
      throw error;
    }
  },
};

export default CatalogService;
